import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import { formatCurrency, formatDate } from 'App/Group/format';
import { confirmCouponChange, reviewCouponChange } from 'App/Coupons/CouponChange/couponChange';
import { Reservation } from 'types/reservations';
import { Ticket, TicketCouponRedemption } from 'types/shared/documents/restaurants/event';

// Coupon-first bookings (pricing version 2): what was promised at payment,
// what changed since, and whether a change still waits for its recalculation.

const redemptionOf = (ticket?: Ticket | null): TicketCouponRedemption | null =>
  ticket?.couponRedemption?.contractVersion === 2 ? ticket.couponRedemption : null;

export const currentCouponCalculation = (ticket?: Ticket | null) => {
  const redemption = redemptionOf(ticket);
  if (!redemption) return null;
  const adjustments = redemption.adjustments ?? [];
  return adjustments.length ? adjustments[adjustments.length - 1].after : redemption;
};

const cents = (value: number) => Math.round(Number(value || 0) * 100);

type Props = { reservation: Reservation; ticket?: Ticket | null };

const CouponPromise: React.FC<Props> = ({ reservation, ticket }) => {
  const { t, i18n } = useTranslation('coupons');
  const lang = i18n.language;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const redemption = redemptionOf(ticket);
  const current = currentCouponCalculation(ticket);
  if (!redemption || !current || !ticket) return null;

  const money = (value: number) => formatCurrency(value, 'EUR', lang);
  const stale = cents(ticket.amount) !== cents(current.grossAmount);
  const lastAdjustment = (redemption.adjustments ?? []).slice(-1)[0];
  const confirmationOwed = !stale && lastAdjustment?.notified === false;
  const rule = redemption.discountRule;
  const ruleText =
    rule.type === 'percentage'
      ? `${Math.round((rule.value <= 1 || redemption.managementVersion ? rule.value * 100 : rule.value) * 100) / 100} %`
      : rule.per === 'perPerson'
        ? t('{{amount}} per guest', { amount: money(rule.value) })
        : t('{{amount}} per booking', { amount: money(rule.value) });

  const recalculate = async () => {
    if (!reservation.id) return;
    setBusy(true);
    setError(null);
    try {
      // Without a proposed change the api compares the saved booking with the promise.
      const decision = await reviewCouponChange(ticket.restaurant, reservation.id, {});
      if (decision && decision !== 'cancelled') {
        await confirmCouponChange(ticket.restaurant, reservation.id, decision);
      }
    } catch {
      setError(t('The recalculation could not be recorded. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box className="ticket-coupon-promise">
      <Typography variant="text-4" weight="bold" block translation={null}>
        {t('Discount code {{code}}: {{rule}}', { code: redemption.code, rule: ruleText })}
      </Typography>
      <Typography variant="text-4" color="subdued" block translation={null}>
        {t('Promised at payment: {{total}} total, {{online}} online, {{later}} at the restaurant.', {
          total: money(redemption.netAmount),
          online: money(redemption.onlineDue),
          later: money(redemption.later),
        })}
      </Typography>
      {(redemption.adjustments ?? []).map((adjustment) => (
        <Typography key={adjustment.at} variant="text-4" color="subdued" block translation={null}>
          {t('{{date}}: recalculated to {{total}}', {
            date: formatDate(new Date(adjustment.at).toISOString().slice(0, 10), lang),
            total: money(adjustment.after.netAmount),
          })}
          {adjustment.goodwill
            ? ` · ${t('goodwill granted')}${adjustment.goodwill.note ? ` („${adjustment.goodwill.note}“)` : ''}`
            : adjustment.unmetConditions.length
              ? ` · ${t('discount withdrawn')}`
              : ''}
          {Math.abs(adjustment.openAmount) >= 0.01 &&
            ` · ${adjustment.openAmount > 0 ? t('{{amount}} open', { amount: money(adjustment.openAmount) }) : t('{{amount}} to refund', { amount: money(-adjustment.openAmount) })}`}
        </Typography>
      ))}
      {confirmationOwed && (
        <Box className="ticket-coupon-promise__stale" role="alert">
          <Typography variant="text-4" block translation="coupons">
            The guest has not received the updated confirmation yet.
          </Typography>
          <Button variant="outlined" translation="coupons" onClick={recalculate} loading={busy} disabled={busy}>
            Send confirmation again
          </Button>
          {error && (
            <Typography variant="text-4" color="error" block translation={null}>
              {error}
            </Typography>
          )}
        </Box>
      )}
      {stale && (
        <Box className="ticket-coupon-promise__stale" role="alert">
          <Typography variant="text-4" block translation="coupons">
            The ticket changed without a recalculation of the discount.
          </Typography>
          <Button variant="outlined" translation="coupons" onClick={recalculate} loading={busy} disabled={busy}>
            Recalculate discount
          </Button>
          {error && (
            <Typography variant="text-4" color="error" block translation={null}>
              {error}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CouponPromise;
